/**
 * EntityVision — Single Responsibility: reglas de visibilidad / fog-of-war.
 *
 * Funciones PURAS: no mutan estado de entidad, no importan Three.js/DOM.
 * Contrato público:
 *   checkBushZones(entity, bushZones) → boolean
 *   shareBushZone(a, b, bushZones) → boolean
 *   canSeeEntity(viewer, target, bushZones, activeWards?, wallBoxes?) → boolean
 *   teamCanSeeEntity(team, target, alliedViewers, bushZones?, activeWards?, wallBoxes?) → boolean
 */

import { hasLineOfSight } from '../vision/lineOfSight.js';

/**
 * @param {{ x: number, z: number }} entity
 * @param {Array<{ x: number, z: number, radius: number }>} bushZones
 * @returns {boolean}
 */
export function checkBushZones(entity, bushZones = []) {
  return bushZones.some((bush) => unitInRadius(entity, bush, bush.radius));
}

/**
 * True when both units occupy at least one shared bush zone (SPY-106).
 * @param {{ x: number, z?: number, y?: number }} a
 * @param {{ x: number, z?: number, y?: number }} b
 * @param {Array<{ x: number, z: number, radius: number }>} bushZones
 */
export function shareBushZone(a, b, bushZones = []) {
  return bushZones.some(
    (bush) => unitInRadius(a, bush, bush.radius) && unitInRadius(b, bush, bush.radius)
  );
}

/**
 * @param {{ team: string, isDead?: boolean, visionRadius?: number, x?: number, z?: number }} viewer
 * @param {{ team: string, isDead?: boolean, isStealthed?: boolean, revealedByHawk?: boolean, revealedByTrueSight?: boolean, x: number, z: number }} target
 * @param {Array<{ x: number, z: number, radius: number }>} bushZones
 * @param {Array<{ team: string, x: number, z: number, visionRadius: number, hasTrueSight?: boolean }>} activeWards
 * @param {Array<{ minX: number, maxX: number, minZ: number, maxZ: number }>} wallBoxes
 * @returns {boolean}
 */
export function canSeeEntity(viewer, target, bushZones = [], activeWards = [], wallBoxes = []) {
  if (!target || target.isDead) return false;
  if (viewer.team === target.team) return true;
  if (target.revealedByHawk) return true;
  if (target.isStealthed && !target.revealedByTrueSight) return false;

  const targetInBush = checkBushZones(target, bushZones);
  if (targetInBush) {
    // Same bush: unit reveal (FID-VIS-03) — ignore short visionRadius.
    if (shareBushZone(viewer, target, bushZones)) return true;
    return alliedWardCovers(target, activeWards, viewer.team, wallBoxes);
  }

  if (typeof viewer.visionRadius === 'number') {
    const inSelf = unitInRadius(target, viewer, viewer.visionRadius)
      && hasClearLos(viewer, target, wallBoxes);
    const inWard = alliedWardCovers(target, activeWards, viewer.team, wallBoxes);
    if (!inSelf && !inWard) return false;
  }

  return true;
}

/**
 * Shared team vision: any living allied viewer (champ, minion, …) can reveal.
 * Wards stay on canSeeEntity via activeWards.
 */
export function teamCanSeeEntity(
  team,
  target,
  alliedViewers = [],
  bushZones = [],
  activeWards = [],
  wallBoxes = []
) {
  if (!target || target.isDead) return false;
  if (target.team === team) return true;
  return (alliedViewers || []).some((viewer) => {
    if (!viewer || viewer.isDead || viewer.team !== team) return false;
    return canSeeEntity(viewer, target, bushZones, activeWards, wallBoxes);
  });
}

function hasClearLos(from, to, wallBoxes) {
  if (!wallBoxes || wallBoxes.length === 0) return true;
  const fz = from.z ?? from.y ?? 0;
  const tz = to.z ?? to.y ?? 0;
  return hasLineOfSight(from.x, fz, to.x, tz, wallBoxes);
}

function unitInRadius(unit, center, radius) {
  const dx = unit.x - center.x;
  const dz = (unit.z ?? unit.y ?? 0) - (center.z ?? center.y ?? 0);
  return Math.hypot(dx, dz) <= radius;
}

function alliedWardCovers(target, activeWards, team, wallBoxes = []) {
  return (activeWards || []).some((w) => {
    if (w.team !== team) return false;
    if (!unitInRadius(target, w, w.visionRadius)) return false;
    return hasClearLos(w, target, wallBoxes);
  });
}
